import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { MapPin, Menu, X, User, LogOut, Calendar, Heart, Settings, LayoutDashboard, Store, Globe, Search, Bell, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { useBranding } from "../../contexts/BrandingContext";

export default function Header({ transparent = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, setUser, logout, isAdmin, isOwner } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { branding } = useBranding();
  const lang = i18n.language;
  const [logoFailed, setLogoFailed] = useState(false);
  const brandName =
    (lang === "el" && branding.brand_name_el) || branding.brand_name || "TableCy";
  const showImage = branding.use_image && branding.logo_url && !logoFailed;
  // reset failure state when admin uploads a new image
  useEffect(() => { setLogoFailed(false); }, [branding.logo_url]);

  const toggleLang = async () => {
    const newLang = i18n.language === "en" ? "el" : "en";
    i18n.changeLanguage(newLang);
    if (user) {
      try {
        const { data } = await axios.patch(`${API}/auth/me`, { preferred_language: newLang });
        setUser?.(data);
      } catch (e) {
        toast.error(t("customer_dashboard:save_failed"));
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (isAdmin) return "/admin";
    if (isOwner) return "/owner";
    return "/account";
  };

  // Role-aware label for the user-menu Dashboard entry. Admins see
  // "Admin Dashboard", venue owners see "Owner Dashboard", and regular
  // customers keep the plain "Dashboard".
  const dashboardLabel = isAdmin
    ? t("nav:admin_dashboard", { defaultValue: "Admin Dashboard" })
    : isOwner
      ? t("nav:owner_dashboard", { defaultValue: "Owner Dashboard" })
      : t("nav:dashboard");

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${transparent ? "bg-transparent" : "bg-background/95 backdrop-blur-sm border-b border-border"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-2 ${transparent ? "text-white" : "text-primary"}`} data-testid="header-logo">
            {showImage ? (
              <img src={branding.logo_url} alt={brandName} className="h-8 w-auto" onError={() => setLogoFailed(true)} />
            ) : branding.logo_svg ? (
              <span
                className="h-8 w-8 inline-flex items-center justify-center"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: branding.logo_svg }}
              />
            ) : (
              <MapPin className="h-7 w-7" />
            )}
            <span className="text-xl font-semibold">{brandName}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/venues" className={`text-sm font-medium transition-colors hover:text-primary ${transparent ? "text-white/90" : "text-foreground"}`} data-testid="header-venues-link">{t("nav:venues")}</Link>
            <Link to="/events" className={`text-sm font-medium transition-colors hover:text-primary ${transparent ? "text-white/90" : "text-foreground"}`} data-testid="header-events-link">{t("nav:events")}</Link>
            <Link to="/map" className={`text-sm font-medium transition-colors hover:text-primary ${transparent ? "text-white/90" : "text-foreground"}`} data-testid="header-map-link">{t("nav:map")}</Link>
            <Link to="/collections" className={`text-sm font-medium transition-colors hover:text-primary ${transparent ? "text-white/90" : "text-foreground"}`} data-testid="header-collections-link">{t("nav:collections")}</Link>
            <SearchBar transparent={transparent} />
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${transparent ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="lang-toggle"
            >
              <Globe className="h-4 w-4" />
              {i18n.language === "en" ? "EL" : "EN"}
            </button>
            {user && <NotificationBell transparent={transparent} />}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={transparent ? "ghost" : "outline"} size="sm" className={transparent ? "text-white border-white/30 hover:bg-white/10" : ""} data-testid="header-user-menu-trigger">
                    <User className="h-4 w-4 mr-2" />
                    {user.name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="cursor-pointer" data-testid="header-dashboard-link">
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {dashboardLabel}
                    </Link>
                  </DropdownMenuItem>
                  {/* Admins get the owner dashboard as a second link so they
                      can hop between back-office and the owner-facing pages
                      without changing accounts. Owners with admin rights
                      already see Admin Dashboard via the primary link. */}
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/owner" className="cursor-pointer" data-testid="header-owner-dashboard-link">
                        <Store className="h-4 w-4 mr-2" />
                        {t("nav:owner_dashboard", { defaultValue: "Owner Dashboard" })}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/account/bookings" className="cursor-pointer" data-testid="header-bookings-link">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t("customer:my_bookings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account/favorites" className="cursor-pointer" data-testid="header-favorites-link">
                      <Heart className="h-4 w-4 mr-2" />
                      {t("customer:favorites")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account/settings" className="cursor-pointer" data-testid="header-settings-link">
                      <Settings className="h-4 w-4 mr-2" />
                      {t("customer:settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="header-logout-button">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("nav:logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button asChild variant={transparent ? "ghost" : "ghost"} size="sm" className={transparent ? "text-white hover:bg-white/10" : ""}>
                  <Link to="/login" data-testid="header-login-link">{t("nav:login")}</Link>
                </Button>
                <Button asChild size="sm" className={transparent ? "bg-white text-primary hover:bg-white/90" : ""}>
                  <Link to="/signup" data-testid="header-signup-link">{t("nav:signup")}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 ${transparent ? "text-white" : "text-foreground"}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="header-mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border animate-slide-down">
          <div className="px-4 py-4 space-y-4">
            <Link
              to="/venues"
              className="block py-2 text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="header-mobile-venues-link"
            >
              {t("nav:venues")}
            </Link>
            <Link
              to="/events"
              className="block py-2 text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="header-mobile-events-link"
            >
              {t("nav:events")}
            </Link>
            <Link
              to="/map"
              className="block py-2 text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("nav:map")}
            </Link>
            <Link
              to="/collections"
              className="block py-2 text-foreground hover:text-primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("nav:collections")}
            </Link>
            <div className="pt-4 border-t border-border space-y-2">
              {user ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="block py-2 text-foreground hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {dashboardLabel}
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/owner"
                      className="block py-2 text-foreground hover:text-primary"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-owner-dashboard-link"
                    >
                      {t("nav:owner_dashboard", { defaultValue: "Owner Dashboard" })}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block py-2 text-destructive"
                  >
                    {t("nav:logout")}
                  </button>
                </>
              ) : (
                <>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>{t("nav:login")}</Link>
                  </Button>
                  <Button asChild className="w-full">
                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>{t("nav:signup")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function SearchBar({ transparent }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search/autocomplete?q=${encodeURIComponent(val)}`);
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch { setSuggestions([]); }
    }, 250);
  };

  const handleSelect = (s) => {
    setOpen(false);
    setQuery("");
    navigate(s.url);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setOpen(false);
      navigate(`/venues?search=${encodeURIComponent(query.trim())}`);
      setQuery("");
    }
  };

  const typeIcons = { venue: MapPin, city: MapPin, cuisine: Search };

  return (
    <div ref={wrapperRef} className="relative" data-testid="header-search-bar">
      <form onSubmit={handleSubmit} className="relative">
        <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${transparent ? "text-white/60" : "text-muted-foreground"}`} />
        <input
          type="text"
          placeholder={t("nav:search_placeholder", { defaultValue: "Search..." })}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          className={`w-36 lg:w-44 pl-8 pr-3 py-1.5 text-sm rounded-full border transition-all focus:w-52 lg:focus:w-56 outline-none ${
            transparent
              ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20"
              : "bg-muted/50 border-border text-foreground placeholder:text-muted-foreground focus:bg-background focus:border-primary/50"
          }`}
          data-testid="header-search-input"
        />
      </form>
      {open && suggestions.length > 0 && (
        <div className="absolute top-full mt-1 left-0 w-64 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50" data-testid="search-suggestions-dropdown">
          {suggestions.map((s, i) => {
            const Icon = typeIcons[s.type] || Search;
            return (
              <button
                key={i}
                onClick={() => handleSelect(s)}
                className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-left hover:bg-muted transition-colors"
                data-testid={`search-suggestion-${i}`}
              >
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{lang === "el" ? (s.label_el || s.label) : s.label}</span>
                <span className="ml-auto text-xs text-muted-foreground capitalize">{s.type}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function NotificationBell({ transparent }) {
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const wrapperRef = useRef(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const localize = (n) => {
    if (lang === "el" && n.title_el) {
      return { title: n.title_el, message: n.message_el || n.message };
    }
    return { title: n.title, message: n.message };
  };

  const fetchNotifs = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications?limit=10`);
      setNotifs(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();

    // Prefer WebSocket for real-time push; fall back to 30s polling if WS fails.
    let ws = null;
    let pollInterval = null;
    let reconnectTimer = null;
    let closed = false;

    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(fetchNotifs, 30000);
    };

    const connectWS = async () => {
      try {
        // Fetch a short-lived ticket over HTTPS (cookies flow reliably here),
        // then open WS with ?token= — avoids ingress issues that strip cookies on WS upgrades.
        let ticket = null;
        try {
          const { data } = await axios.get(`${API}/notifications/ws-ticket`);
          ticket = data?.ticket;
        } catch {
          /* fall back to cookie-only auth */
        }
        const base = API.replace(/^http/, "ws") + "/notifications/ws";
        const wsUrl = ticket ? `${base}?token=${encodeURIComponent(ticket)}` : base;
        ws = new WebSocket(wsUrl);
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.event === "connected" && typeof msg.unread_count === "number") {
              setUnreadCount(msg.unread_count);
            } else if (msg.event === "notification" && msg.payload) {
              setNotifs((prev) => [msg.payload, ...prev].slice(0, 10));
              setUnreadCount((c) => c + 1);
              // Inline toast in the user's current language
              const { title, message } = localize(msg.payload);
              try { toast.success(title, { description: message }); } catch {}
            }
          } catch {}
        };
        ws.onclose = () => {
          if (closed) return;
          // Fallback to polling, attempt WS reconnect after 10s
          startPolling();
          reconnectTimer = setTimeout(() => {
            if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
            connectWS();
          }, 10000);
        };
        ws.onerror = () => { try { ws && ws.close(); } catch {} };
      } catch {
        startPolling();
      }
    };

    connectWS();
    return () => {
      closed = true;
      if (ws) { try { ws.close(); } catch {} }
      if (pollInterval) clearInterval(pollInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setUnreadCount(0);
      setNotifs(notifs.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/notifications`);
      setNotifs([]);
      setUnreadCount(0);
      setConfirmClear(false);
      toast.success(t("notifications:cleared", { defaultValue: "Notifications cleared" }));
    } catch {
      toast.error(t("notifications:clear_failed", { defaultValue: "Failed to clear notifications" }));
    }
  };

  const handleDeleteOne = async (e, notifId) => {
    // Per-row delete fires from a child button — stop propagation so we
    // don't also trigger the row's mark-as-read + navigate handler.
    e.stopPropagation();
    e.preventDefault();
    try {
      await axios.delete(`${API}/notifications/${notifId}`);
      setNotifs((prev) => prev.filter((n) => n.notification_id !== notifId));
      // If we deleted an unread one, decrement
      const wasUnread = notifs.find((n) => n.notification_id === notifId && !n.is_read);
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      toast.error(t("notifications:delete_failed", { defaultValue: "Failed to delete notification" }));
    }
  };

  const handleClickNotif = async (notif) => {
    if (!notif.is_read) {
      try {
        await axios.put(`${API}/notifications/${notif.notification_id}/read`);
        setUnreadCount(Math.max(0, unreadCount - 1));
        setNotifs(notifs.map(n => n.notification_id === notif.notification_id ? { ...n, is_read: true } : n));
      } catch {}
    }
    if (notif.link) {
      setOpen(false);
      window.location.href = notif.link;
    }
  };

  const typeIcons = {
    booking: Calendar,
    booking_update: Calendar,
    time_proposal: Calendar,
    proposal_accepted: Calendar,
    proposal_declined: Calendar,
    reward: Heart,
    review: Heart,
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="notification-bell-wrapper">
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifs(); }}
        className={`relative p-2 rounded-full transition-colors ${transparent ? "text-white/80 hover:bg-white/10" : "text-muted-foreground hover:bg-muted"}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-unread-count">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-popover border border-border rounded-xl shadow-xl overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
            <span className="text-sm font-medium">{t("notifications:title", { defaultValue: "Notifications" })}</span>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read-btn">
                  {t("notifications:mark_all_read", { defaultValue: "Mark all read" })}
                </button>
              )}
              {notifs.length > 0 && (
                <button
                  onClick={() => setConfirmClear(true)}
                  className="text-xs text-muted-foreground hover:text-destructive hover:underline flex items-center gap-1"
                  data-testid="clear-all-notifications-btn"
                  title={t("notifications:clear_all", { defaultValue: "Clear all" })}
                >
                  <Trash2 className="h-3 w-3" />
                  {t("notifications:clear_all", { defaultValue: "Clear all" })}
                </button>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length > 0 ? notifs.map((n) => {
              const Icon = typeIcons[n.type] || Bell;
              const localized = localize(n);
              return (
                <div
                  key={n.notification_id}
                  className={`relative group flex items-start gap-3 hover:bg-muted transition-colors border-b border-border/50 ${!n.is_read ? "bg-primary/5" : ""}`}
                  data-testid={`notif-${n.notification_id}`}
                >
                  <button
                    onClick={() => handleClickNotif(n)}
                    className="flex-1 text-left px-4 py-3 flex items-start gap-3 min-w-0"
                  >
                    <div className={`mt-0.5 p-1.5 rounded-lg shrink-0 ${!n.is_read ? "bg-primary/10" : "bg-muted"}`}>
                      <Icon className={`h-3.5 w-3.5 ${!n.is_read ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.is_read ? "font-medium" : ""}`}>{localized.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{localized.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{n.created_at?.split("T")[0]}</p>
                    </div>
                    {!n.is_read && <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0" />}
                  </button>
                  <button
                    onClick={(e) => handleDeleteOne(e, n.notification_id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 mr-1 mt-1 text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`delete-notif-${n.notification_id}`}
                    title={t("notifications:delete", { defaultValue: "Delete" })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            }) : (
              <div className="text-center py-8 text-sm text-muted-foreground">{t("notifications:empty", { defaultValue: "No notifications yet" })}</div>
            )}
          </div>
        </div>
      )}

      {/* In-page confirmation for the destructive Clear-all action.
          Replaces window.confirm() so the UX matches the Delete User /
          Delete Review patterns elsewhere in the app. */}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("notifications:clear_all_title", { defaultValue: "Clear all notifications" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("notifications:confirm_clear_all", { defaultValue: "Delete all notifications? This cannot be undone." })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-all-cancel">
              {t("notifications:cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAll}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="clear-all-confirm"
            >
              {t("notifications:clear_all", { defaultValue: "Clear all" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
